// frontend/src/widgets/ClientContactSummaryWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-14
// Updated:      2026-09-07 16:03 BST
//
// Purpose:
//   Provides the customer and primary-contact context for the currently selected
//   opportunity in the Opportunities workspace.
//
// Role:
//   This is a read-only contextual widget. It resolves the selected opportunity's
//   customer and primary contact using the canonical authenticated API and gives
//   the user immediate access to useful customer/contact details and safe browser
//   actions such as email, telephone, and website links.
//
// Lifecycle Status:
//   Active. This widget is registered as `client_contact_summary` on the
//   Opportunities surface and is part of the Opportunities default layout.
//
// Change Summary (2026-09-07):
//   - Replaced the January prototype's legacy endpoint cascade with canonical
//     `/api/customers/{id}` and `/api/contacts` routes only.
//   - Removed fallback requests to legacy `/api/clients/**` routes.
//   - Reduced contact resolution to the minimum required request path:
//       1) opportunity primary_contact_id when available;
//       2) customer primary_contact_id when available;
//       3) one customer-scoped contacts query to find is_primary=true.
//   - Added explicit states for no selected opportunity, no linked customer,
//     no primary contact, loading, and API errors.
//   - Added useful customer/contact context fields and safe Email / Call /
//     Website actions without introducing new backend mutations.
//   - Added manual Refresh for retrying context loads.
//   - Added mobile-responsive single-column presentation and touch targets.
//   - Added mobile ReactGridLayout host-height synchronisation so the complete
//     widget remains visible without the desktop resize handle.
//   - Preserves canonical server-side tenant resolution; tenant_id is never sent.
//
// Operational Notes:
//   - The widget remains driven exclusively by the `opportunity` prop supplied
//     by OpportunitiesPage.
//   - No customer/contact mutation is performed here.
//   - Do not reintroduce client-supplied tenant context or broad unscoped
//     customer/contact fetches.
//   - A missing primary contact is displayed as a data condition, not silently
//     replaced with an arbitrary contact.
//
// Future Intent:
//   Keep this widget as the compact CRM context panel for Opportunities.
//   If richer customer/contact navigation is introduced later, route actions
//   should be added through the canonical application navigation layer rather
//   than duplicating customer/contact editors inside this widget.

import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const MOBILE_BREAKPOINT = 768;

const pickFirst = (...values) => {
  for (const value of values) {
    if (value === 0) return 0;
    if (
      value !== null &&
      value !== undefined &&
      String(value).trim() !== ""
    ) {
      return value;
    }
  }
  return null;
};

const toPositiveInt = (value) => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};

const dash = (value) =>
  value === null || value === undefined || String(value).trim() === ""
    ? "—"
    : String(value);

const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const normaliseUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

const ActionLink = ({ href, children, theme, external = false }) => {
  if (!href) return null;

  return (
    <a
      href={href}
      {...(external
        ? {
            target: "_blank",
            rel: "noopener noreferrer",
          }
        : {})}
      style={{
        minHeight: 44,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "9px 12px",
        borderRadius: 8,
        background: theme?.button_general?.normal || "#4b5563",
        color: "#fff",
        textDecoration: "none",
        fontSize: 13,
        fontWeight: 800,
        fontFamily: "var(--font-body)",
        boxSizing: "border-box",
      }}
    >
      {children}
    </a>
  );
};

const DetailRow = ({ label, value, isMobile }) => (
  <>
    <div
      style={{
        fontSize: 12,
        fontWeight: 800,
        opacity: 0.68,
        whiteSpace: isMobile ? "normal" : "nowrap",
      }}
    >
      {label}
    </div>
    <div
      style={{
        minWidth: 0,
        fontSize: 13,
        fontWeight: 700,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      }}
    >
      {dash(value)}
    </div>
  </>
);

export default function ClientContactSummaryWidget({ opportunity }) {
  const { theme } = useTheme();

  const rootRef = useRef(null);

  const [customer, setCustomer] = useState(null);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customerError, setCustomerError] = useState("");
  const [contactError, setContactError] = useState("");
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false
  );

  const customerId = useMemo(
    () =>
      toPositiveInt(
        pickFirst(
          opportunity?.customer_id,
          opportunity?.client_id,
          opportunity?.customerId,
          opportunity?.clientId
        )
      ),
    [opportunity]
  );

  const opportunityPrimaryContactId = useMemo(
    () =>
      toPositiveInt(
        pickFirst(
          opportunity?.primary_contact_id,
          opportunity?.primaryContactId,
          opportunity?.contact_id,
          opportunity?.contactId
        )
      ),
    [opportunity]
  );

  const opportunityLabel = useMemo(
    () =>
      pickFirst(
        opportunity?.name,
        opportunity?.title,
        opportunity?.opportunity_name,
        opportunity?.reference,
        opportunity?.internal_reference,
        opportunity?.display_name
      ),
    [opportunity]
  );

  useEffect(() => {
    const updateViewportMode = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadContext = async () => {
      setCustomer(null);
      setContact(null);
      setCustomerError("");
      setContactError("");

      if (!opportunity || !customerId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      let customerRow = null;
      let resolvedContactId = opportunityPrimaryContactId;

      try {
        const customerResponse = await api.get(
          `/api/customers/${customerId}`
        );

        if (cancelled) return;

        customerRow = customerResponse?.data || null;
        setCustomer(customerRow);
      } catch (error) {
        if (cancelled) return;

        setCustomerError(
          error?.response?.data?.detail ||
            error?.message ||
            "Failed to load customer."
        );
      }

      if (cancelled) return;

      if (!resolvedContactId && customerRow) {
        resolvedContactId = toPositiveInt(
          pickFirst(
            customerRow?.primary_contact_id,
            customerRow?.primaryContactId,
            customerRow?.contact_id,
            customerRow?.contactId
          )
        );
      }

      if (resolvedContactId) {
        try {
          const contactResponse = await api.get(
            `/api/contacts/${resolvedContactId}`
          );

          if (cancelled) return;
          setContact(contactResponse?.data || null);
        } catch (error) {
          if (cancelled) return;

          setContactError(
            error?.response?.data?.detail ||
              error?.message ||
              "Failed to load primary contact."
          );
          setContact(null);
        }
      } else {
        try {
          const contactsResponse = await api.get("/api/contacts", {
            params: {
              customer_id: customerId,
              limit: 200,
              offset: 0,
            },
          });

          if (cancelled) return;

          const contacts = listFrom(contactsResponse?.data).filter(
            (row) =>
              String(row?.customer_id ?? row?.client_id ?? "") ===
              String(customerId)
          );

          const primary =
            contacts.find((row) => row?.is_primary === true) || null;

          setContact(primary);

          if (!primary) {
            setContactError("");
          }
        } catch (error) {
          if (cancelled) return;

          setContactError(
            error?.response?.data?.detail ||
              error?.message ||
              "Failed to resolve primary contact."
          );
          setContact(null);
        }
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    loadContext();

    return () => {
      cancelled = true;
    };
  }, [
    opportunity,
    customerId,
    opportunityPrimaryContactId,
    refreshNonce,
  ]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest(".react-grid-item");
    if (!gridHost) return undefined;

    const previousHeight = gridHost.style.height;
    const previousMinHeight = gridHost.style.minHeight;

    let frameId = null;
    let observer = null;

    const restoreDesktopHeight = () => {
      gridHost.style.height = previousHeight;
      gridHost.style.minHeight = previousMinHeight;
    };

    const syncMobileHostHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        if (!isMobile) {
          restoreDesktopHeight();
          return;
        }

        const hostRect = gridHost.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const topOffset = Math.max(0, rootRect.top - hostRect.top);
        const requiredHeight = Math.ceil(topOffset + root.scrollHeight + 8);

        gridHost.style.height = `${requiredHeight}px`;
        gridHost.style.minHeight = `${requiredHeight}px`;
      });
    };

    syncMobileHostHeight();
    window.addEventListener("resize", syncMobileHostHeight);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncMobileHostHeight);
      observer.observe(root);
    }

    return () => {
      window.removeEventListener("resize", syncMobileHostHeight);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      restoreDesktopHeight();
    };
  }, [
    isMobile,
    loading,
    customer,
    contact,
    customerError,
    contactError,
    opportunity,
  ]);

  const customerName = pickFirst(
    customer?.name,
    customer?.company_name,
    customer?.companyName,
    opportunity?.customer_name,
    opportunity?.client_name
  );

  const customerReference = pickFirst(
    customer?.internal_reference,
    customer?.reference,
    customer?.account_reference,
    customer?.company_number
  );

  const customerEmail = pickFirst(
    customer?.email,
    customer?.primary_email,
    customer?.primaryEmail
  );

  const customerPhone = pickFirst(
    customer?.phone_number,
    customer?.phone,
    customer?.telephone,
    customer?.tel,
    customer?.phoneNumber
  );

  const customerWebsite = pickFirst(
    customer?.website,
    customer?.website_url,
    customer?.url
  );

  const contactName = pickFirst(
    contact?.full_name,
    contact?.fullName,
    [contact?.title, contact?.first_name, contact?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim(),
    [contact?.firstName, contact?.lastName].filter(Boolean).join(" ").trim()
  );

  const contactRole = pickFirst(
    contact?.job_title,
    contact?.jobTitle,
    contact?.role,
    contact?.department
  );

  const contactEmail = pickFirst(
    contact?.email,
    contact?.primary_email,
    contact?.primaryEmail
  );

  const contactMobile = pickFirst(
    contact?.mobile_number,
    contact?.mobile,
    contact?.mobileNumber,
    contact?.phone_number,
    contact?.phone
  );

  const contactPreferredMethod = pickFirst(
    contact?.preferred_contact_method,
    contact?.preferredContactMethod
  );

  const cardStyle = {
    background: theme?.card_bg || "#fff",
    borderRadius: 14,
    padding: isMobile ? 12 : 14,
    boxShadow: theme?.card_shadow || "0 1px 2px rgba(0,0,0,0.06)",
    border: theme?.card_border || "1px solid rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflow: isMobile ? "visible" : "hidden",
  };

  const sectionStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: isMobile ? 12 : 11,
    minWidth: 0,
    background: "#fff",
  };

  const detailGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "96px minmax(0, 1fr)"
      : "118px minmax(0, 1fr)",
    rowGap: 8,
    columnGap: 10,
    alignItems: "start",
    minWidth: 0,
  };

  const actionsStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : "repeat(auto-fit, minmax(110px, max-content))",
    gap: 8,
    marginTop: 10,
  };

  if (!opportunity) {
    return (
      <div ref={rootRef} style={cardStyle}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 900,
            color: theme?.accent_color || "#111827",
          }}
        >
          Client & Contact Summary
        </div>
        <div
          style={{
            border: "1px dashed #d1d5db",
            borderRadius: 10,
            padding: "18px 14px",
            color: "#6b7280",
            fontSize: 13,
            textAlign: "center",
          }}
        >
          Select an opportunity to view its customer and primary contact.
        </div>
      </div>
    );
  }

  if (!customerId) {
    return (
      <div ref={rootRef} style={cardStyle}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 900,
            color: theme?.accent_color || "#111827",
          }}
        >
          Client & Contact Summary
        </div>

        {opportunityLabel && (
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              overflowWrap: "anywhere",
            }}
          >
            Opportunity: <strong>{opportunityLabel}</strong>
          </div>
        )}

        <div
          style={{
            border: "1px solid #fde68a",
            background: "#fffbeb",
            color: "#92400e",
            borderRadius: 10,
            padding: "12px",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          This opportunity does not currently have a customer linked to it.
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} style={cardStyle}>
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 900,
              color: theme?.accent_color || "#111827",
            }}
          >
            Client & Contact Summary
          </div>

          {opportunityLabel && (
            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                color: "#6b7280",
                overflowWrap: "anywhere",
              }}
            >
              Opportunity: <strong>{opportunityLabel}</strong>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setRefreshNonce((value) => value + 1)}
          disabled={loading}
          style={{
            minHeight: 44,
            width: isMobile ? "100%" : "auto",
            padding: "9px 12px",
            border: "none",
            borderRadius: 8,
            background: theme?.button_general?.normal || "#4b5563",
            color: "#fff",
            fontSize: 13,
            fontWeight: 800,
            fontFamily: "var(--font-body)",
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.65 : 1,
          }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {loading && !customer && (
        <div
          style={{
            border: "1px solid #dbeafe",
            background: "#eff6ff",
            color: "#1d4ed8",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Loading customer and primary contact…
        </div>
      )}

      <section style={sectionStyle}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 900,
            color: "#111827",
            marginBottom: 10,
          }}
        >
          Customer
        </div>

        {customerError ? (
          <div
            style={{
              color: theme?.danger || "#b91c1c",
              fontSize: 12,
              fontWeight: 700,
              marginBottom: 10,
              overflowWrap: "anywhere",
            }}
          >
            {customerError}
          </div>
        ) : null}

        <div style={detailGridStyle}>
          <DetailRow label="Name" value={customerName} isMobile={isMobile} />
          <DetailRow
            label="Reference"
            value={customerReference}
            isMobile={isMobile}
          />
          <DetailRow label="Email" value={customerEmail} isMobile={isMobile} />
          <DetailRow label="Phone" value={customerPhone} isMobile={isMobile} />
          <DetailRow
            label="Website"
            value={customerWebsite}
            isMobile={isMobile}
          />
        </div>

        {(customerEmail || customerPhone || customerWebsite) && (
          <div style={actionsStyle}>
            <ActionLink
              href={
                customerEmail
                  ? `mailto:${encodeURIComponent(String(customerEmail))}`
                  : ""
              }
              theme={theme}
            >
              Email Customer
            </ActionLink>

            <ActionLink
              href={
                customerPhone
                  ? `tel:${String(customerPhone).replace(/\s+/g, "")}`
                  : ""
              }
              theme={theme}
            >
              Call Customer
            </ActionLink>

            <ActionLink
              href={normaliseUrl(customerWebsite)}
              theme={theme}
              external
            >
              Open Website
            </ActionLink>
          </div>
        )}
      </section>

      <section style={sectionStyle}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 900,
            color: "#111827",
            marginBottom: 10,
          }}
        >
          Primary Contact
        </div>

        {contactError ? (
          <div
            style={{
              color: theme?.danger || "#b91c1c",
              fontSize: 12,
              fontWeight: 700,
              marginBottom: 10,
              overflowWrap: "anywhere",
            }}
          >
            {contactError}
          </div>
        ) : null}

        {!loading && !contact && !contactError ? (
          <div
            style={{
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              color: "#6b7280",
              borderRadius: 8,
              padding: "10px",
              fontSize: 12,
            }}
          >
            No primary contact is assigned to this customer.
          </div>
        ) : (
          <div style={detailGridStyle}>
            <DetailRow label="Name" value={contactName} isMobile={isMobile} />
            <DetailRow label="Role" value={contactRole} isMobile={isMobile} />
            <DetailRow label="Email" value={contactEmail} isMobile={isMobile} />
            <DetailRow
              label="Mobile"
              value={contactMobile}
              isMobile={isMobile}
            />
            <DetailRow
              label="Preferred"
              value={contactPreferredMethod}
              isMobile={isMobile}
            />
          </div>
        )}

        {contact && (contactEmail || contactMobile) && (
          <div style={actionsStyle}>
            <ActionLink
              href={
                contactEmail
                  ? `mailto:${encodeURIComponent(String(contactEmail))}`
                  : ""
              }
              theme={theme}
            >
              Email Contact
            </ActionLink>

            <ActionLink
              href={
                contactMobile
                  ? `tel:${String(contactMobile).replace(/\s+/g, "")}`
                  : ""
              }
              theme={theme}
            >
              Call Contact
            </ActionLink>
          </div>
        )}
      </section>
    </div>
  );
}
